// ABOUTME: Result modal for bulk actions that returns per-item success/failure outcomes.
// ABOUTME: Shows succeeded/failed counts and a scrollable list of failed identifiers, each with its reason and shipper payload.
import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../theme";
import SHModal from "../../theme/Modal/SHModal";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import ShCopyButton from "../../theme/ShCopy";
import ShPayloadToggle from "../../theme/ShPayloadToggle";

const MBulkActionResult = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();

    const failedIds = props.failedIds ?? [];
    // Callers either pass bare identifiers or items carrying the reason ({ id, error, errors, payload }).
    const failedItems = props.failedItems ?? failedIds.map((failedId) => ({ id: failedId }));
    const succeeded = props.succeeded ?? 0;
    const failedListLabel = props.failedListLabel ?? t('failed_return_barcodes', { defaultValue: 'Mislukte retour-barcodes' });

    return (
        <SHModal
            open={props.open}
            onClose={props.handleClose}
        >
            <Box
                sx={{
                    textAlign: "center",
                    paddingY: 4,
                    paddingX: 3,
                    pb: 3,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                }}
            >
                <Subheading>{props.title}</Subheading>
                <Box
                    sx={{
                        width: 1,
                        display: 'flex',
                        gap: 2,
                    }}
                >
                    <Box
                        sx={{
                            borderRadius: 5,
                            height: 32,
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            pl: 2,
                            pr: 0.5,
                            background: colors.green[100],
                        }}
                    >
                        <Text semibold variant="green">
                            {t("success")}
                        </Text>
                        <Box
                            sx={{
                                background: colors.green[400],
                                color: theme.palette.mode === "dark" ? colors.txt['primaryDark'] : colors.txt['light'],
                                fontSize: 14,
                                fontWeight: "bold",
                                px: 2,
                                height: 24,
                                borderRadius: 4,
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            {succeeded}
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            borderRadius: 5,
                            height: 32,
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            pl: 2,
                            pr: 0.5,
                            background: colors.red[100],
                        }}
                    >
                        <Text semibold variant="red">
                            {t("failed")}
                        </Text>
                        <Box
                            sx={{
                                background: colors.red[400],
                                color: theme.palette.mode === "dark" ? colors.txt['primaryDark'] : colors.txt['light'],
                                fontSize: 14,
                                fontWeight: "bold",
                                px: 2,
                                height: 24,
                                borderRadius: 4,
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            {failedItems.length}
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding: 3,
                    borderRadius: 4,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >
                <Text bold>
                    {failedListLabel}
                </Text>
                <Box
                    sx={{
                        maxHeight: 280,
                        overflowY: 'auto',
                        background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                        borderRadius: 4,
                        padding: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                    }}
                >
                    {failedItems.map((item, idx) => {
                        const reasons = item.errors?.length > 0 ? item.errors : (item.error ? [item.error] : []);
                        const reasonText = reasons.join("\n");
                        return (
                            <Box
                                key={`${item.id ?? ""}-${idx}`}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                }}
                            >
                                <Text bold>{item.id}</Text>
                                {reasons.length > 0 &&
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1,
                                            pl: 2,
                                            pr: 0.5,
                                            py: 0.5,
                                            borderRadius: 5,
                                            background: colors.red[100],
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.5,
                                            }}
                                        >
                                            {reasons.map((reason, ridx) => (
                                                <Text key={`${item.id ?? ""}-${idx}-${ridx}`} semibold variant="red">{reason}</Text>
                                            ))}
                                        </Box>
                                        <ShCopyButton
                                            styles={{button:{
                                                background: colors.red[100],
                                                color: colors.red[400],
                                                "&:hover" : {
                                                    background: colors.red[400],
                                                    color: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt['light'],
                                                }
                                            }}}
                                            closeAfter={1500}
                                            value={reasonText}
                                        />
                                    </Box>
                                }
                                <ShPayloadToggle payload={item.payload} align="right" sx={{ mt: 1.5 }} />
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </SHModal>
    );
};

export default MBulkActionResult;
